import { Options, SDK, sdkCache } from '../index';
import {
    Avatar,
    LikeProfileInput,
    OptionPoll,
    DailySong,
    ProfileText,
    PredefinedMessage,
    PredefinedMessageInput,
    SendMessageResult,
    OptionPollAnswerInput,
    ProfileOptionPoll,
    QueryFindManyAvatarArgs,
    ResultSuccess,
} from '../generated/graphql';
import { likeProfile } from '../features/likeProfile';
import { InMemoryCache } from '@apollo/client';
import { optionPollAnswer, optionPolls } from '../features/optionPoll';
import { findManyAvatar } from '../features/avatar';
import { findManyDailySong } from '../features/dailySong';
import { findManyProfileText } from '../features/profileText';
import {
    findManyPredefinedMessage,
    sendPredefinedMessage,
} from '../features/predefinedMessage';
import { FetchPolicy } from '@apollo/client/core/watchQueryOptions';

interface ZoondaSDKInterface extends SDK {
    optionPollAnswer: (
        data: OptionPollAnswerInput,
    ) => Promise<ResultSuccess | ProfileOptionPoll>;
    likeProfile: (data: LikeProfileInput) => Promise<ResultSuccess>;
    sendPredefinedMessage: (
        data: PredefinedMessageInput,
    ) => Promise<SendMessageResult | ResultSuccess>;
    optionPolls: (
        fetchPolicy?: FetchPolicy,
    ) => Promise<Array<OptionPoll> | ResultSuccess>;
    findManyAvatar: (
        args?: QueryFindManyAvatarArgs,
        fetchPolicy?: FetchPolicy,
    ) => Promise<Array<Avatar> | ResultSuccess>;
    findManyDailySong: (
        fetchPolicy?: FetchPolicy,
    ) => Promise<Array<DailySong> | ResultSuccess>;
    findManyProfileText: (
        fetchPolicy?: FetchPolicy,
    ) => Promise<Array<ProfileText> | ResultSuccess>;
    findManyPredefinedMessage: (
        fetchPolicy?: FetchPolicy,
    ) => Promise<Array<PredefinedMessage> | ResultSuccess>;
}

const init = (x: ZoondaSDKInterface): ZoondaSDK => {
    x.optionPollAnswer = async (
        data: OptionPollAnswerInput,
    ): Promise<ResultSuccess | ProfileOptionPoll> => {
        return optionPollAnswer(data, x);
    };
    x.likeProfile = async (data: LikeProfileInput): Promise<ResultSuccess> => {
        return likeProfile(data, x);
    };
    x.sendPredefinedMessage = async (
        data: PredefinedMessageInput,
    ): Promise<SendMessageResult | ResultSuccess> => {
        return sendPredefinedMessage(data, x);
    };
    x.findManyPredefinedMessage = async (
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<PredefinedMessage> | ResultSuccess> => {
        return findManyPredefinedMessage(x, fetchPolicy);
    };
    x.optionPolls = async (
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<OptionPoll> | ResultSuccess> => {
        return optionPolls(x, fetchPolicy);
    };
    x.findManyAvatar = async (
        args?: QueryFindManyAvatarArgs,
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<Avatar> | ResultSuccess> => {
        return findManyAvatar(x, args, fetchPolicy);
    };
    x.findManyDailySong = async (
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<DailySong> | ResultSuccess> => {
        return findManyDailySong(x, fetchPolicy);
    };
    x.findManyProfileText = async (
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<ProfileText> | ResultSuccess> => {
        return findManyProfileText(x, fetchPolicy);
    };
    return x;
};

export class ZoondaSDK extends SDK implements ZoondaSDKInterface {
    static async Init(
        options: Options,
        errorCallback: (message: unknown) => unknown,
    ): Promise<ZoondaSDK> {
        const cache = await sdkCache();
        return new ZoondaSDK(options, cache, errorCallback);
    }

    constructor(
        options: Options,
        cache: InMemoryCache,
        errorCallback: (message: unknown) => unknown,
    ) {
        super(options, cache, errorCallback, 'zoonda');
        return init(this);
    }

    optionPollAnswer = async (
        data: OptionPollAnswerInput,
    ): Promise<ResultSuccess | ProfileOptionPoll> => {
        return optionPollAnswer(data, this);
    };

    findManyPredefinedMessage = async (
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<PredefinedMessage> | ResultSuccess> => {
        return findManyPredefinedMessage(this, fetchPolicy);
    };

    sendPredefinedMessage = async (
        data: PredefinedMessageInput,
    ): Promise<SendMessageResult | ResultSuccess> => {
        return sendPredefinedMessage(data, this);
    };

    findManyProfileText = async (
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<ProfileText> | ResultSuccess> => {
        return findManyProfileText(this, fetchPolicy);
    };
    findManyAvatar = async (
        args?: QueryFindManyAvatarArgs,
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<Avatar> | ResultSuccess> => {
        return findManyAvatar(this, args, fetchPolicy);
    };
    optionPolls = async (
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<OptionPoll> | ResultSuccess> => {
        return optionPolls(this, fetchPolicy);
    };

    likeProfile = async (data: LikeProfileInput): Promise<ResultSuccess> => {
        return likeProfile(data, this);
    };

    findManyDailySong = async (
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<DailySong> | ResultSuccess> => {
        return findManyDailySong(this, fetchPolicy);
    };
}
